import React from 'react';
import PropTypes from "prop-types";
import { makeStyles } from '@material-ui/core/styles';
import clsx from "clsx";

import I18n from "../../base/i18n.js";

import Tooltip from "../../base/components/tooltip.js";

import InvestmentStatusEnum from "../../enums/investment_status.js";
import InvestmentActivityStatusEnum from "../../enums/investment_activity_status.js";
import InvestmentStatusDescriptionEnum from "../../enums/investment_status_description.js";

import MoneyFormatter from "../../utils/money_formatter.js";
import DateTimeFormatter from "../../utils/date_time_formatter.js";

const blockStyles = makeStyles(theme => ({
  root: {
  },
  tooltip: {
    fontSize: "14px",
    padding: "5px",
    textAlign: "center"
  },
  investmentStatus: {
    fontSize: "12px",
    color: theme.palette.greenMiddle,
    lineHeight: "1",
    marginTop: "-5px",
    padding: "11px 0",
    textAlign: "center",
    textTransform: "uppercase",
    ['@media (max-width: 768px)']: {
      padding: "5px",
      fontSize: "10px",
      lineHeight: "20px"
    },
  },
  applied: {
    background: "rgba(157, 198, 71, 0.2)"
  },
  paid: {
    background: "rgba(182, 123, 100, 0.2)"
  },
  paidBack: {
    background: theme.palette.grayBg
  },
  canceled: {
    background: theme.palette.gray2,
    color: "white"
  },
  info: {
    marginTop: "15px",
    padding: "0 20px",
  },
  date: {
    fontSize: "12px",
    color: theme.palette.grayText,
    lineHeight: "1",
    marginBottom: "10px"
  },
  amountRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    lineHeight: "1.2",
    fontSize: "16px",
  },
  interest: {
    color: theme.palette.blueMain,
  },
}));

/**
 * Display info about investment of user
 * @name ParticipantsInvestmentInfo
 * @param {object} props React component properties
 * @param {string} [props.className] CSS-class of container
 * @param {object.<string, string>} [props.classes] CSS-classes for inner elements. Accept keys:
 *    - tooltip
 *    - info
 *    - investmentStatus
 *    - date
 *    - amountRow
 *    - myInvestment
 *    - interest
 *    - text
 *    - showMore
 * @param {object} [props.investment] Info about investment from API
 * @example
 * return (
 *  <ParticipantsInvestmentInfo investment = { investment }
 *                              className = { "class-name" }
 *                              classes = { {
 *                                info: "info",
 *                                date: "date",
 *                                myInvestment: "my-investment"
 *                              } }  />
 * )
*/

const ParticipantsInvestmentInfo = (props) => {
  const { investment, className, classes : externalClasses = {} } = props;

  if(!investment) {
    return <React.Fragment />
  }

  const { status, activityStatus, amount, calculatedInterest, createdAt } = investment;
  const classes = blockStyles();

  return (
    <div className = { clsx(classes.root, className) } >
      {
        status &&
        <div className = { clsx({
          [classes.investmentStatus]: true,
          [externalClasses.investmentStatus]: true,
          [classes.applied]: investment.status === "applied",
          [classes.paid]: investment.status === "paid",
          [classes.paidBack]: investment.status === "paid_back",
          [classes.canceled]: investment.activityStatus === "canceled"
        })}>
          { activityStatus === "canceled" ?
            InvestmentActivityStatusEnum.label(activityStatus) :
            <Tooltip content = {
              <div className = { classes.tooltip }>
                { InvestmentStatusDescriptionEnum.label(status) }
              </div>
            }>
              { InvestmentStatusEnum.label(status) }
            </Tooltip>
          }
        </div>
      }
      <div className = { clsx(classes.info, externalClasses.info)  } >
        { createdAt &&
          <div className = { clsx(classes.date, externalClasses.date) }>
            { `${I18n.t("component.participants_investment_info.investment_date")}: ${DateTimeFormatter.format(createdAt)}` }
          </div>
        }
        <div className = { clsx(classes.amountRow, externalClasses.amountRow) }>
          {
            amount &&
            <span className = { clsx(classes.myInvestment, externalClasses.myInvestment) }>
              { `${I18n.t("component.participants_investment_info.my_investment")}: ${MoneyFormatter.format(amount)} ${I18n.t("currency.euro")}` }
            </span>
          }
          {/* {
            calculatedInterest && activityStatus !== "canceled" &&
            <span className = { clsx(classes.interest, externalClasses.interest) }>
              { `+${MoneyFormatter.format(calculatedInterest)} ${I18n.t("currency.euro")}` }
            </span>
          } */}
        </div>
      </div>
    </div>
  )
}

ParticipantsInvestmentInfo.propTypes = {
  classes: PropTypes.shape({
    tooltip: PropTypes.string,
    info: PropTypes.string,
    investmentStatus: PropTypes.string,
    date: PropTypes.string,
    amountRow: PropTypes.string,
    myInvestment: PropTypes.string,
    interest: PropTypes.string
  }),
}

export default ParticipantsInvestmentInfo;
